import React from "react";
import { Link } from "react-router-dom";
import { Flame, Music, Users, TrendingUp, ArrowRight, Heart, ShieldCheck, Headphones, Sparkles, Code2, Mic2 } from "lucide-react";

/**
 * About — the company story page.
 *
 * Brutalist editorial layout in the 1o1 visual language:
 * massive Cabinet Grotesk headline, numeric kickers, vermilion accents,
 * diagonal stripe noise behind the hero, left-aligned spread.
 */

const Kicker = ({ num, label }) => (
  <p className="text-[11px] uppercase tracking-[0.28em] font-black mb-3 flex items-center gap-2">
    <span className="text-vermilion">{num}</span>
    <span className="text-white/40">{label}</span>
  </p>
);

const PillarCard = ({ num, icon: Icon, title, body }) => (
  <div className="relative bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 rounded-sm p-6 hover:border-vermilion/40 transition group">
    <p className="text-[10px] tracking-[0.28em] font-black text-vermilion mb-3">{num}</p>
    <Icon size={24} className="text-white/80 mb-3 group-hover:text-vermilion transition" strokeWidth={2} />
    <h3 className="font-display text-xl font-black uppercase tracking-tight leading-tight mb-2">{title}</h3>
    <p className="text-sm text-white/55 leading-relaxed">{body}</p>
  </div>
);

const StatTile = ({ value, label, sub }) => (
  <div className="relative">
    <p className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-none text-vermilion">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-black mt-2">{label}</p>
    {sub && <p className="text-xs text-white/60 mt-1">{sub}</p>}
  </div>
);

const About = () => {
  return (
    <div data-testid="about-page" className="relative">
      {/* Hero */}
      <section className="relative pt-6 pb-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-6 right-0 w-[420px] h-[420px] opacity-20 rotate-12"
          style={{ background: "repeating-linear-gradient(45deg, #E84525 0 6px, transparent 6px 14px)" }}
        />
        <Kicker num="01" label="The Company" />
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.92] mb-6 max-w-4xl">
          Built by <span className="text-vermilion">artists</span>,<br />for artists.
        </h1>
        <p className="text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl">
          1o1sound is a music streaming platform that pays the people who actually
          make the music. Ninety cents of every dollar a listener spends with us
          flows back to the artists — not to a label, not to a holding company,
          not to a private-equity rollup. Independence on purpose.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/premium"
            data-testid="about-cta-premium"
            className="inline-flex items-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white text-[12px] font-black uppercase tracking-[0.12em] px-5 py-3 rounded-sm transition shadow-[0_12px_36px_-8px_rgba(232,69,37,0.5)]"
          >
            <Flame size={14} /> Get Premium <ArrowRight size={14} />
          </Link>
          <Link
            to="/upload"
            data-testid="about-cta-artists"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-vermilion text-white text-[12px] font-black uppercase tracking-[0.12em] px-5 py-3 rounded-sm transition"
          >
            <Music size={14} /> For artists <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Stats strip */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 border-y border-white/8 py-10 mb-16">
        <StatTile value="90%" label="To the artist" sub="Of every stream dollar" />
        <StatTile value="$0.20" label="Gross / stream" sub="$0.18 net to artist" />
        <StatTile value="$10" label="Listener Premium" sub="2 months free" />
        <StatTile value="3 mo" label="Artist trial" sub="Free, no card" />
      </section>

      {/* Three pillars */}
      <section className="mb-20">
        <Kicker num="02" label="What we stand for" />
        <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-8 max-w-2xl">
          Three rules we don't break.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PillarCard
            num="01"
            icon={Heart}
            title="Artists eat first"
            body="The 90/10 split is non-negotiable. Payouts run on Stripe Connect Express, ledgered in real time so artists can see every cent."
          />
          <PillarCard
            num="02"
            icon={ShieldCheck}
            title="No dark patterns"
            body="No buried renewals, no surprise fees, no fake-scarcity timers. Cancel in two taps. Free users can still listen — with ads — forever."
          />
          <PillarCard
            num="03"
            icon={Sparkles}
            title="Independent forever"
            body="We're funded by listeners, not labels. No license deals that override the catalog. The artist owns the audience they build here."
          />
        </div>
      </section>

      {/* Founder note */}
      <section className="mb-20">
        <Kicker num="03" label="The story" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-3">
              Why we exist.
            </h2>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-vermilion">
              A note from the team
            </p>
          </div>
          <div className="md:col-span-2 space-y-4 text-white/70 text-[15px] leading-[1.75]">
            <p>
              Major streaming platforms pay the average independent artist less than half
              a cent per stream. By the time the math reaches a working musician, there's
              nothing left to make next month's rent — let alone the next record.
            </p>
            <p>
              We built 1o1sound the way we wished streaming worked when we were paying our
              own rent on tour. Higher payouts, transparent ledgers, free access for
              listeners who can't afford a subscription, and a real artist studio that
              treats uploads, lyrics, and promo campaigns like first-class features.
            </p>
            <p>
              It's a streaming app, sure. But it's really a small bet that the music
              business can still be built around the people who make the music.
            </p>
            <p className="text-vermilion font-display font-black tracking-tight">
              — The 1o1sound team
            </p>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="mb-20">
        <Kicker num="04" label="Inside the app" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <PillarCard num="01" icon={Headphones} title="Full music player" body="Drag-to-expand Now Playing, time-synced lyrics, queue control, shuffle/repeat, lossless on the roadmap." />
          <PillarCard num="02" icon={TrendingUp} title="Hotspot discovery" body="Live discovery surface that flags rising independent artists daily. Find the next thing before the algorithm does." />
          <PillarCard num="03" icon={Users} title="Real artist profiles" body="Auto-fetched artist photos from public sources, follower counts, payout history, badge embeds for socials." />
          <PillarCard num="04" icon={Mic2} title="Artist studio" body="Upload MP3 + cover art, run promo campaigns, view streams + payouts in real time. Built into the app, not bolted on." />
          <PillarCard num="05" icon={Code2} title="Open APIs" body="iTunes, Deezer, LRCLIB, YouTube fallback. Open data sources for open discovery. No locked-down license walls." />
          <PillarCard num="06" icon={Flame} title="Fire (not Like)" body="Tiny detail, big intent. You don't 'like' a song you can't stop playing — you set it on fire." />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 pt-12 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/premium"
            data-testid="about-bottom-premium"
            className="group block border border-white/10 hover:border-vermilion rounded-sm p-8 transition relative overflow-hidden"
          >
            <Flame size={20} className="text-vermilion mb-3" />
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-1">Listen on us first</h3>
            <p className="text-sm text-white/55 mb-4">Premium is $10/mo with 2 months free.</p>
            <p className="text-vermilion text-[12px] font-black uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Start trial <ArrowRight size={14} />
            </p>
          </Link>
          <Link
            to="/jobs"
            data-testid="about-bottom-jobs"
            className="group block border border-white/10 hover:border-vermilion rounded-sm p-8 transition relative overflow-hidden"
          >
            <Code2 size={20} className="text-vermilion mb-3" />
            <h3 className="font-display text-2xl font-black uppercase tracking-tight mb-1">Work with us</h3>
            <p className="text-sm text-white/55 mb-4">Producer and engineer roles open now.</p>
            <p className="text-vermilion text-[12px] font-black uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              See open roles <ArrowRight size={14} />
            </p>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
